import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PostStyle1 from "../Components/PostStyle1";
import PostStyle2 from "../Components/PostStyle2";
import { FaTh, FaList } from "react-icons/fa";
import shopify from "../../Images/shopify.png";
import "../style.css";
import FilterSidebar from "./FilterSidebar";

const ActivePostsSection = () => {
  const [blocks, setBlocks] = useState([]); // Fetched blocks
  const [filteredBlocks, setFilteredBlocks] = useState([]); // Filtered and sorted blocks
  const [categories, setCategories] = useState([]); // Categories for filtering
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [selectedStyle, setSelectedStyle] = useState("Style 2"); // Display style
  const [viewMode, setViewMode] = useState("Details"); // View mode
  const [sortBy, setSortBy] = useState("Newest"); // Sorting preference
  const [searchTerm, setSearchTerm] = useState(""); // Search term from sidebar
  const [selectedCategory, setSelectedCategory] = useState("All"); // Selected category
  const [appMode, setAppMode] = useState(false); // Filter by app

  const navigate = useNavigate();

  // Fetch blocks from the API on component mount
  useEffect(() => {
    const fetchBlocks = async () => {
      try {
        const response = await fetch("https://xebrand.in/Backend/get_data.php");
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success) {
          const activeBlocks = data.data.filter((block) => block.status === "Active");
          setBlocks(activeBlocks);
          setFilteredBlocks(activeBlocks); // Initialize filtered blocks

          // Extract unique categories dynamically
          const uniqueCategories = [
            "All",
            ...new Set(activeBlocks.map((block) => block.category || "Misc")),
          ];
          setCategories(uniqueCategories);
        } else {
          setError(data.message || "Failed to fetch blocks.");
        }
      } catch (err) {
        console.error("Error fetching blocks:", err);
        setError("An error occurred while fetching blocks.");
      } finally {
        setLoading(false);
      }
    };

    fetchBlocks();
  }, []);

  // Check if a post is "new" (within 2 days)
  const isNew = (block) => {
    const createdDate = new Date(block.created_at);
    const currentDate = new Date();
    const diffInDays = Math.floor((currentDate - createdDate) / (1000 * 60 * 60 * 24));
    return diffInDays <= 2;
  };

  // Shuffle function to randomize blocks
  const shuffleArray = (array) => {
    let shuffledArray = [...array];
    for (let i = shuffledArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
    }
    return shuffledArray;
  };

  // Apply sorting, filtering, and randomization when dependencies change
  useEffect(() => {
    let newBlocks = [];
    let regularBlocks = [];

    // Separate "New" blocks and regular blocks
    blocks.forEach((block) => {
      if (isNew(block)) {
        newBlocks.push(block);
      } else {
        regularBlocks.push(block);
      }
    });

    // Sort "New" blocks by date (newest first)
    newBlocks.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    // Shuffle regular blocks
    regularBlocks = shuffleArray(regularBlocks);

    // Combine "New" blocks at the top with shuffled regular blocks
    let combinedBlocks = [...newBlocks, ...regularBlocks];



    // Apply sorting
    if (sortBy === "Popularity") {
      combinedBlocks.sort((a, b) => b.views - a.views);
    }

    setFilteredBlocks(combinedBlocks);
  }, [blocks, sortBy, selectedCategory, appMode]);




  // Handle category & search term filtering from sidebar
  const handleFilterChange = (category, search) => {
    setSelectedCategory(category);
    setSearchTerm(search);
  };

  // Apply filtering, sorting, and search when dependencies change
  useEffect(() => {
    let filtered = [...blocks];

    // Filter by category
    if (selectedCategory !== "All") {
      filtered = filtered.filter((block) => block.category === selectedCategory);
    }

    // Filter by search term (case insensitive search in title & description)
    if (searchTerm.trim() !== "") {
      filtered = filtered.filter(
        (block) =>
          (block.title && block.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (block.description && block.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Separate "New" and regular posts
    let newBlocks = filtered.filter(isNew);
    let regularBlocks = filtered.filter((block) => !isNew(block));

    // Sort "New" posts by date (newest first)
    newBlocks.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    // Shuffle regular posts
    regularBlocks = shuffleArray(regularBlocks);

    // Combine "New" posts (top) + shuffled regular posts
    let sortedBlocks = [...newBlocks, ...regularBlocks];

    // Apply sorting (popularity or newest)
    if (sortBy === "Popularity") {
      sortedBlocks.sort((a, b) => b.views - a.views);
    }

    setFilteredBlocks(sortedBlocks);
  }, [blocks, selectedCategory, searchTerm, sortBy]);
  
  // Navigate to the post details page
  const handlePostClick = (block) => {
    navigate(`/Shopify-custom-sections/post/${block.slug}`); // Use slug in the URL
  };

  // Render loading state
  if (loading) {
    return (
      <div className="loading-spinner">
        <p>Loading....</p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="error-message">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="customer-blocks-container" id="ShowCase">
      {/* Header */}
      <div className="customer-blocks-header">
        <h2 className="customer-blocks-title">Custom Blocks</h2>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-wrap">
          <div className="filter-cont">
            {/* Styles Dropdown */}
            <div className="filter-group filt-1">
              <label className="filter-label">Styles:</label>
              <select
                className="filter-select"
                value={selectedStyle}
                onChange={(e) => setSelectedStyle(e.target.value)}
              >
                <option value="Style 1">Style 1</option>
                <option value="Style 2">Style 2</option>
              </select>
            </div>

            {/* Category Dropdown */}
            <div className="filter-group filt-2">
              <label className="filter-label">Category:</label>
              <select
                className="filter-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort By Dropdown */}
            <div className="filter-group filt-3">
              <label className="filter-label">Sort by:</label>
              <select
                className="filter-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="Newest">Newest</option>
                <option value="Popularity">Popularity</option>
              </select>
            </div>

            {/* View Toggle */}
            <div className="filter-group">
              <label className="filter-label">View:</label>
              <div className="view-toggle">
                <button
                  className={`view-button ${viewMode === "Details" ? "active" : ""}`}
                  onClick={() => setViewMode("Details")}
                >
                  <FaList />
                </button>
                <button
                  className={`view-button ${viewMode === "Cards" ? "active" : ""}`}
                  onClick={() => setViewMode("Cards")}
                >
                  <FaTh />
                </button>
              </div>
            </div>
          </div>

          {/* App Mode Toggle */}
          <div className="filter-cont">
            <div className="filter-group">
              <label className="filter-label">App:</label>
              <div
                className={`app-img ${appMode ? "active" : ""}`}
                onClick={() => setAppMode(!appMode)}
              >
                <img src={shopify} alt="App" className="circular-toggle-image" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="filter-grid-showcase">
      <FilterSidebar posts={blocks} onFilter={handleFilterChange} />
        {/* Posts Grid */}
        <div className="customer-blocks-grid">
          {filteredBlocks.length > 0 ? (
            filteredBlocks.map((block) =>
              selectedStyle === "Style 1" ? (
                <div
                  className="post-1"
                  key={block.id}
                  onClick={() => handlePostClick(block)}
                >
                  <PostStyle1 block={block} viewMode={viewMode} />
                </div>
              ) : (
                <div
                  className="post-2"
                  key={block.id}
                  onClick={() => handlePostClick(block)}
                >
                  <PostStyle2 block={block} viewMode={viewMode} />
                </div>
              )
            )
          ) : (
            <p className="customer-blocks-empty">No active blocks available.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivePostsSection;
